import React from 'react';
import {ScrollView, View} from 'react-native';
import {observer} from 'mobx-react-lite';

import AddToWallet from '../../components/rewards/AddToWallet';
import MemberCard from '../../components/rewards/MemberCard';
import SafeAreaPadding from '../../components/SafeAreaPadding';
import Button from '../../components/themed/Button';
import Text from '../../components/themed/Text';
import ContentView from '../../components/ui-controls/layout/ContentView';
import logoImg from '../../images/brand/header-logo.png';
import {AppRoute} from '../../models/AppRoute';
import {RouteNavigatorScreenProps} from '../../models/navigationTypes';
import {reset} from '../../navigation/RootNavigation';
import {useStores} from '../../stores/use-stores';
import {resolveMemberDisplayName, rewardsMarketingName} from '../../utils/rewards';

const RewardsSuccess = observer(
  ({
    route,
    navigation,
  }: RouteNavigatorScreenProps<AppRoute.REWARDS_SUCCESS | AppRoute.REWARDS_ACTIVATE_SUCCESS>) => {
    const {rewardsStore, userStore, shopSelectionStore} = useStores();

    const flow = route?.params?.flow;

    const cardNumber = rewardsStore.cardNumber || '';
    const firstName = userStore.firstName || rewardsStore.firstName || '';
    // DIS-1245: prefer rewardsStore (API/form name from lite signup) over
    // userStore (only populated for full-session users). Subsumes the
    // DIS-1187 defensive composition — the helper also handles the
    // userStore.name `" "` truthy-empty quirk that broke the old fallback.
    const memberName = resolveMemberDisplayName(
      {firstName: rewardsStore.firstName, lastName: rewardsStore.lastName},
      {firstName: userStore.firstName, lastName: userStore.lastName},
      'Success',
    );
    const walletUrl = rewardsStore.walletUrl || '';

    return (
      <ScrollView style={{backgroundColor: 'white'}}>
        <ContentView>
          <Text variant="heading1">HERE’S YOUR {rewardsMarketingName.toUpperCase()} CARD</Text>

          <Text variant="body2">
            {firstName ? `Nice one, ${firstName}!` : 'Welcome!'} You are now a part of our
            {` ${rewardsMarketingName} `}
            program! Show this barcode at checkout to collect awesome rewards!
          </Text>
          <View style={{gap: 8, marginTop: 24}}>
            <MemberCard
              logoImg={logoImg}
              cardNumber={cardNumber}
              name={memberName}
              memberSince={rewardsStore.memberSince}
              isStaff={rewardsStore.isStaffSignup}
              isLmgStaff={rewardsStore.isLmgStaff}
            />
            {/* DIS-1556: Zen only issues the consumer card template, so the wallet
                pass never generates for staff members. Hide the dead button. */}
            {!rewardsStore.isStaffSignup && !rewardsStore.isLmgStaff && (
              <AddToWallet walletUrl={walletUrl} />
            )}
          </View>

          {flow !== 'activate' && (
            <>
              <Text style={{fontWeight: 600, marginTop: 40}} variant="body2">
                Check your email
              </Text>
              <Text variant="body2" style={{marginTop: 16}}>
                Although your account is ready to go, we have sent you an email to complete the last
                few steps and give you access to our website to see all of your member benefits.
              </Text>
            </>
          )}

          <View style={{gap: 8, marginTop: 40}}>
            <Button
              secondary
              // DIS-1682: the LMG staff arm predates DIS-1628 (added by DIS-1384) and
              // was missed when the label changed. A lite staff member holds no session
              // by design, so `isLmgStaffFlow` promised REWARDS to someone who is not
              // signed in — the exact confusion DIS-1628 exists to remove. Session state
              // is the only thing that decides this label; the onPress below already
              // routes logged-out users through sign-in either way.
              title={userStore.isLoggedIn ? 'GO TO REWARDS' : 'SIGN IN / SIGN UP'}
              onPress={() => {
                // DIS-1384 R18 / DIS-1628: Logged-out users go via sign-in first, then
                // land on rewards (mirrors RewardsRedirectScreen.handleGoToRewards, DIS-1094).
                if (!userStore.isLoggedIn) {
                  userStore.setPostLoginReturnToScreen(
                    AppRoute.ROOT as never,
                    {
                      screen: AppRoute.MAIN,
                      params: {screen: AppRoute.ACCOUNT_REWARDS},
                    } as never,
                  );
                  navigation.navigate(
                    AppRoute.ROOT as never,
                    {
                      screen: AppRoute.MAIN,
                      params: {screen: AppRoute.ACCOUNT_NOT_LOGGED_IN},
                    } as never,
                  );
                  return;
                }

                const isStoreSelected = !!shopSelectionStore?.selectedStore?.id;
                if (isStoreSelected) {
                  reset({
                    index: 0,
                    routes: [
                      {
                        name: AppRoute.ROOT,
                        state: {
                          routes: [
                            {
                              name: AppRoute.MAIN,
                              state: {
                                routes: [{name: AppRoute.REWARDS}],
                              },
                            },
                          ],
                        },
                      },
                    ],
                  });
                } else {
                  // No store selected — ROOT is not mounted.
                  // Reset to store selection; after picking a store, ROOT mounts with HOME.
                  reset({
                    index: 0,
                    routes: [{name: AppRoute.ROOT_STORE_SELECTION_FLOW}],
                  });
                }
              }}
            />
          </View>
          {/* DIS-1628: the rewards flow is a ROOT sibling of DrawerNavigator, so no
              ancestor applies the bottom inset — without this the CTA sits behind the
              Android system nav bar and the iOS home indicator. */}
          <SafeAreaPadding bottom />
        </ContentView>
      </ScrollView>
    );
  },
);

export default RewardsSuccess;
